import React, { useEffect, useState } from "react";
import { ICustoEstadia } from "../../types/types";
import { FrontendNotification } from "../../../../../../shared/Notification";
import api from "../../../../../../services/api";
import SelectCustom from "../../../../../../components/SelectCustom";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";

interface Option {
  value: number;
  label: string;
}

interface ITransportadoraData {
  id_transportadora: number;
  razao_social: string;
}

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: ICustoEstadia;
  onConfirm: () => void;
  onClose: () => void;
}

const Form: React.FC<Props> = ({ isEdit, selectedRow, onConfirm, onClose }) => {
  const [custoHora, setCustoHora] = useState<string>(String(selectedRow?.custo_hora ?? 0));
  const [custoMeiaDiaria, setCustoMeiaDiaria] = useState<string>(String(selectedRow?.custo_meia_diaria ?? 0));
  const [custoDiaria, setCustoDiaria] = useState<string>(String(selectedRow?.custo_diaria ?? 0));
  const [idTransportadora, setIdTransportadora] = useState<number | undefined>(selectedRow?.id_transportadora ?? undefined);
  const [transportadorasOptions, setTransportadorasOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const formatCurrency = (value: string): string => {
    // Remove tudo que não for número
    const onlyNumbers = value.replace(/\D/g, '');
    
    if (!onlyNumbers || onlyNumbers === '0') return '';
    
    // Converte para número dividindo por 100 (para centavos)
    const numberValue = parseFloat(onlyNumbers) / 100;
    
    // Formata como moeda brasileira
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(numberValue);
  };

  

  useEffect(() => {
    const fetchTransportadoras = async () => {
      try {
        const body = {
          qtd_por_pagina: 100,
          order_by: "data_historico",
          order_direction: "desc",
        };

        const response = await api.post("/listar/transportadoras", body);

        const mappingData = response.data.data.map((rows: ITransportadoraData) => {
          return {
            value: rows.id_transportadora,
            label: rows.razao_social,
          };
        });

        setTransportadorasOptions(mappingData);
      } catch (error) {
        console.error("Erro ao buscar transportadoras:", error);
        FrontendNotification("Erro ao carregar transportadoras", "error");
      }
    };
    fetchTransportadoras();
  }, []);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);

      const body: Partial<ICustoEstadia> = {
        custo_hora: Number(custoHora) / 100, // Converte centavos para reais
        custo_meia_diaria: Number(custoMeiaDiaria) / 100,
        custo_diaria: Number(custoDiaria) / 100,
        id_transportadora: idTransportadora ?? undefined,
        id_usuario_historico: user?.id,
      };

      if (isEdit && selectedRow?.id_custo_estadia) {
        body.id_custo_estadia = selectedRow.id_custo_estadia;
        await api.post("/editar/custoEstadia", body);
        FrontendNotification("Custo de estadia atualizado com sucesso", "success");
      } else {
        await api.post("/cadastrar/custoEstadia", body);
        FrontendNotification("Custo de estadia salvo com sucesso", "success");
      }
      onConfirm();
      setLoading(false);
      onClose();
    } catch (error: any) {
      setLoading(false);
      // Verifica se é erro de violação de chave única (duplicidade de transportadora)
      if (error?.response?.data?.error_msg?.includes('duplicate key value violates unique constraint')) {
        FrontendNotification("Transportadora já possui custo estadia cadastrado.", "error");
      } else {
        FrontendNotification("Erro ao salvar custo de estadia", "error");
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col">
          <span className="text-sm mb-1">Custo Hora (R$)</span>
          <input 
            type="text" 
            value={formatCurrency(custoHora)} 
            onChange={(e) => {
              const inputValue = e.target.value;
              const onlyNumbers = inputValue.replace(/\D/g, '');
              setCustoHora(onlyNumbers);
            }} 
            className="border bg-white px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#062D4E]"
            placeholder="R$ 0,00"
          />
        </label>

        <label className="flex flex-col">
          <span className="text-sm mb-1">Custo Meia Diária (R$)</span>
          <input 
            type="text" 
            value={formatCurrency(custoMeiaDiaria)} 
            onChange={(e) => {
              const inputValue = e.target.value;
              const onlyNumbers = inputValue.replace(/\D/g, '');
              setCustoMeiaDiaria(onlyNumbers);
            }} 
            className="border bg-white px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#062D4E]"
            placeholder="R$ 0,00"
          />
        </label>

        <label className="flex flex-col">
          <span className="text-sm mb-1">Custo Diária (R$)</span>
          <input 
            type="text" 
            value={formatCurrency(custoDiaria)} 
            onChange={(e) => {
              const inputValue = e.target.value;
              const onlyNumbers = inputValue.replace(/\D/g, '');
              setCustoDiaria(onlyNumbers);
            }} 
            className="border bg-white px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#062D4E]"
            placeholder="R$ 0,00"
          />
        </label>

        <label className="flex flex-col">
          <SelectCustom
            title="Transportadora (opcional)"
            data={transportadorasOptions}
            onChange={(selectedOption: Option) => setIdTransportadora(selectedOption.value)}
            value={idTransportadora}
          />
        </label>
      </div>

      <div className="flex justify-end mt-6 gap-2">
        <button type="button" onClick={onClose} className="px-6 py-2 rounded-full border border-[#062D4E] text-[#062D4E] bg-white hover:bg-[#F0F4F8] transition">Cancelar</button>
        <button type="submit" disabled={loading} className="px-6 py-2 rounded-full bg-[#062D4E] text-white hover:bg-[#094077] transition">{loading ? "Salvando..." : "Salvar"}</button>
      </div>
    </form>
  );
};

export default Form;
